import { createSlice } from '@reduxjs/toolkit';
import {
    entityKey,
    paymentMethods,
    stepIDs,
    subscriptionDocStatus,
    subscriptionStatus,
} from 'utils/constants';
import { mergeWith } from 'lodash';
import { arrayLodashCustomizer, getEntityIdFromIRI } from 'utils/helpers';

const initialState = {
    internalUser: null,
    holdUp: false, // this is used to block loggedInStatus check
    logoutOpen: false,
    logoutLoading: false,
    subscriptionFormStatus: subscriptionDocStatus.INEXISTENT,
    signatureDialog: {
        open: false,
        subscription: null,
        convention: null,
    },
    shareCategory: null,
    roleChoice: null,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        internalUserReplaced(state, action) {
            state.internalUser = action.payload;
        },
        holdUpSet(state, action) {
            state.holdUp = action.payload;
        },
        logoutOpen(state, action) {
            state.logoutOpen = true;
        },
        logoutClosed(state, action) {
            state.logoutOpen = false;
        },
        logoutLoadingSet(state, action) {
            state.logoutLoading = action.payload || false;
        },
        subscriptionFormStatusSet(state, action) {
            state.subscriptionFormStatus = action.payload;
        },
        userTunnelStepsUpdated(state, action) {
            // updating the userTunnelSteps for the investor's ongoing subscription
            const ongoingSubscriptionIndex = state.internalUser?.subscriptions.findIndex(
                s => s.subscriptionStatus === subscriptionStatus.IN_PROGRESS
            );
            if (ongoingSubscriptionIndex !== -1) {
                state.internalUser.subscriptions[ongoingSubscriptionIndex].userTunnelSteps =
                    action.payload.subscriptions[ongoingSubscriptionIndex].userTunnelSteps;
            }
        },
        newSubscriptionCreated(state, action) {
            state.internalUser.subscriptions = [
                ...state.internalUser.subscriptions,
                action.payload.subscriptions[state.internalUser.subscriptions.length],
            ];
        },
        userSubscriptionDeleted(state, action) {
            state.internalUser.subscriptions = state.internalUser.subscriptions.filter(
                s => s.id !== action.payload
            );
        },
        companyConventionsUpdated(state, action) {
            state.internalUser.distributorCompany.conventions =
                action.payload.distributorCompany.conventions;
            state.internalUser.subscriptions[0].distributorCompany.conventions =
                action.payload.distributorCompany.conventions;
        },
        investorProfileUpdated(state, action) {
            state.internalUser.investorProfile = {
                ...state.internalUser.investorProfile,
                ...action.payload,
            };
        },
        distributorCompanyUpdated(state, action) {
            state.internalUser.distributorCompany = action.payload;
            state.internalUser.subscriptions[0].distributorCompany = action.payload;
        },
        userUpdated(state, action) {
            // updates the user data in the store, changed from /my-account page
            const { user, profile } = action.payload;

            state.internalUser = {
                ...state.internalUser,
                firstName: user.firstName,
                lastName: user.lastName,
                phoneNumber: user.phoneNumber,
                title: user.title,
                birthName: user.birthName,
                email: user.email,
                investorProfile: {
                    ...state.internalUser.investorProfile,
                    ...user.investorProfile,
                    birthLocation: {
                        ...state.internalUser.investorProfile.birthLocation,
                        ...user.investorProfile.birthLocation,
                    },
                    currentLocation: {
                        ...state.internalUser.investorProfile.currentLocation,
                        ...user.investorProfile.currentLocation,
                    },
                },
            };
            if (profile === 'PM') {
                state.internalUser.investorCompany = {
                    ...state.internalUser.investorCompany,
                    ...user.investorCompany,
                    addressLocation: {
                        ...state.internalUser.investorCompany.addressLocation,
                        ...user.investorCompany.addressLocation,
                    },
                    postalLocation: {
                        ...state.internalUser.investorCompany.postalLocation,
                        ...user.investorCompany.postalLocation,
                    },
                };
            }
        },
        coSubscriberUpdated(state, action) {
            // updates the co-subscriber user data in the store, changed from /my-account page or tunnel
            state.internalUser.subscriptions = state.internalUser.subscriptions.map(s => ({
                ...s,
                coSubscriber: {
                    ...s.coSubscriber,
                    ...action.payload.subscriptions[0].coSubscriber,
                    investorProfile: {
                        ...s.coSubscriber?.investorProfile,
                        ...action.payload.subscriptions[0].coSubscriber.investorProfile,
                    },
                },
            }));
        },
        registerResubmitted(state, action) {
            const { user, profile } = action.payload;

            // all profiles update these fields
            state.internalUser = {
                ...state.internalUser,
                firstName: user.firstName,
                lastName: user.lastName,
                title: user.title,
                birthName: user.birthName,
                email: user.email,
                investorProfile: {
                    ...state.internalUser.investorProfile,
                    emailAgreement: user.investorProfile.emailAgreement,
                    subscribedToNewsletter: user.investorProfile.subscribedToNewsletter,
                    termsAgreement: user.investorProfile.termsAgreement,
                },
            };

            // update the userTunnelSteps
            const subscriptionIndex = user.subscriptions.findIndex(
                s => s.id === user.inProgressSubscription.id
            );
            state.internalUser.subscriptions[subscriptionIndex].userTunnelSteps =
                user.subscriptions[subscriptionIndex].userTunnelSteps;
            state.internalUser.subscriptions[subscriptionIndex].subscriptionDocuments =
                user.subscriptions[subscriptionIndex].subscriptionDocuments;

            if (profile === 'PM') {
                state.internalUser.investorCompany = {
                    ...state.internalUser.investorCompany,
                    corporateForm: user.investorCompany.corporateForm,
                    corporateName: user.investorCompany.corporateName,
                };
            }
            if (profile === 'DIST') {
                state.internalUser.distributorCompany = {
                    ...state.internalUser.distributorCompany,
                    ...user.distributorCompany,
                };
            }
        },
        // updates everything on the logged in / substituted user's ongoing subscription
        // to be used for now after most tunnel actions
        tunnelSubscriptionUpdated(state, action) {
            const {
                payload: {
                    distributorCompany,
                    investorCompany,
                    investorProfile,
                    subscriptions,
                    ...payload
                },
            } = action;
            // update the internalUser fields
            state.internalUser = {
                ...state.internalUser,
                ...payload,
                distributorCompany: state.internalUser.distributorCompany
                    ? state.internalUser.distributorCompany
                    : distributorCompany,
                investorCompany: investorCompany
                    ? { ...state.internalUser.investorCompany, ...investorCompany }
                    : null,
                investorProfile: {
                    ...state.internalUser.investorProfile,
                    ...investorProfile,
                },
            };

            // updating the ongoing subscription
            const ongoingSubscriptionIndex = state.internalUser?.subscriptions.findIndex(
                s => s.subscriptionStatus === subscriptionStatus.IN_PROGRESS
            );
            if (ongoingSubscriptionIndex !== -1) {
                state.internalUser.subscriptions[ongoingSubscriptionIndex] = mergeWith(
                    state.internalUser.subscriptions[ongoingSubscriptionIndex],
                    subscriptions[ongoingSubscriptionIndex],
                    arrayLodashCustomizer
                );
            }
        },
        signatureDialogSet(state, action) {
            const { open, subscription = null, convention = null } = action.payload;
            state.signatureDialog = { open, subscription, convention };
        },
        shareCategorySet(state, action) {
            state.shareCategory = action.payload;
        },
        roleChoiceSet(state, action) {
            state.roleChoice = action.payload;
        },
    },
});

export const {
    internalUserReplaced,
    holdUpSet,
    logoutOpen,
    logoutClosed,
    logoutLoadingSet,
    subscriptionFormStatusSet,
    userTunnelStepsUpdated,
    newSubscriptionCreated,
    userSubscriptionDeleted,
    companyConventionsUpdated,
    investorProfileUpdated,
    distributorCompanyUpdated,
    userUpdated,
    coSubscriberUpdated,
    registerResubmitted,
    tunnelSubscriptionUpdated, // updates everything regarding ongoing subscription
    signatureDialogSet,
    shareCategorySet,
    roleChoiceSet,
} = userSlice.actions;

const getCurrentSubscription = ({ user: { internalUser } }) => {
    const currentSubscription = internalUser?.subscriptions?.find(
        sub => sub.subscriptionStatus === subscriptionStatus.FINISHED
    );
    if (currentSubscription) {
        return currentSubscription;
    }
    return internalUser?.subscriptions?.[0] || null;
};

const getTunnelSubscription = ({ user: { internalUser } }) =>
    internalUser?.subscriptions?.find(
        sub => sub.subscriptionStatus === subscriptionStatus.IN_PROGRESS
    ) || null;

const getUserTunnelSteps = state => {
    const tunnelSubscription = getTunnelSubscription(state);
    const usesDirectDebit =
        tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT;
    if (!tunnelSubscription) return [];
    return (
        getTunnelSubscription(state)?.userTunnelSteps?.filter(
            ts =>
                usesDirectDebit ||
                ts.tunnelStep.step.id !== stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE
        ) || []
    );
};

const getDistributorLogoUrl = state =>
    state.user.internalUser?.distributorCompany?.companyLogo?.url || '';

const getSubscriptionAlerts = ({ user: { internalUser } }) => {
    const subscription = internalUser?.subscriptions?.find(
        sub => sub.subscriptionStatus === subscriptionStatus.IN_PROGRESS
    );

    return subscription
        ? subscription.userTunnelSteps?.reduce((acc, crt) => {
              if (
                  crt.tunnelStep.step.id !== stepIDs.ID_CHECK &&
                  crt.tunnelStep.step.id !== stepIDs.SECOND_ID_CHECK &&
                  crt.tunnelStep.step.id !== stepIDs.ADDRESS_CHECK &&
                  crt.tunnelStep.step.id !== stepIDs.RIB_CHECK
              ) {
                  return {
                      ...acc,
                      [crt.tunnelStep.step.id]:
                          crt.tunnelStep.step.id === stepIDs.CUSTOM_STEP
                              ? [
                                    ...(acc[crt.tunnelStep.step.id] || []),
                                    ...crt.alerts.filter(a => a.blocking && !a.solved),
                                ]
                              : crt.alerts.filter(a => a.blocking && !a.solved),
                  };
              }
              return acc;
          }, {})
        : {};
};

const getIndirectSubscriptionStatus = ({ user: { internalUser } }) =>
    !!internalUser?.subscriptions?.find(
        sub => !!sub.createdBy && sub.subscriptionStatus === subscriptionStatus.FINISHED
    );

const getLastFinishedSubscription = ({ user: { internalUser } }) => {
    if (internalUser?.subscriptions?.length > 0) {
        return (
            internalUser.subscriptions.findLast(
                sub => sub.subscriptionStatus === subscriptionStatus.FINISHED
            ) || internalUser.subscriptions[0]
        );
    }
    return null;
};

const getFinishedSubscriptionStatus = ({ user: { internalUser } }) =>
    !!internalUser?.subscriptions?.find(
        sub => sub.subscriptionStatus === subscriptionStatus.FINISHED
    );

const getInProgressSubscriptionStatus = ({ user: { internalUser } }) =>
    !!internalUser?.subscriptions?.find(
        sub => sub.subscriptionStatus === subscriptionStatus.IN_PROGRESS
    );

export const selectors = {
    getInternalUser: state => state.user.internalUser,
    getHoldUp: state => state.user.holdUp,
    getLogoutDialogState: state => state.user.logoutOpen,
    getLogoutLoader: state => state.user.logoutLoading,
    getSubscriptionFormStatus: state => state.user.subscriptionFormStatus,
    getLoggedInStatus: state => !!state.user.internalUser && !state.user.holdUp,
    getSignatureDialogState: state => state.user.signatureDialog,
    getRegenerateFlag: state => state.user.regenerateFlag,
    getShareSubcategoryId: state =>
        typeof state.user.shareCategory?.shareSubcategories?.[0] === 'object'
            ? state.user.shareCategory?.shareSubcategories?.[0].id
            : getEntityIdFromIRI(
                  state.user.shareCategory?.shareSubcategories?.[0], // always get the first subcateogory
                  entityKey.SUBCATEGORY
              ),
    getShareCategory: state => state.user.shareCategory,
    getRoleChoice: state => state.user.roleChoice,
    getCurrentSubscription,
    getTunnelSubscription,
    getUserTunnelSteps,
    getDistributorLogoUrl,
    getSubscriptionAlerts,
    getIndirectSubscriptionStatus,
    getFinishedSubscriptionStatus,
    getInProgressSubscriptionStatus,
    getLastFinishedSubscription,
};

export default userSlice.reducer;
